import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import TeachingPlanTiles from '../components/TeachingPlanTiles';
import AppLayout from '../components/AppLayout';
import RoutineTimetableView from '../components/RoutineTimetableView';
import { Card, CardContent } from '../components/ui/card';
import { Icons } from '../lib/Icons';
import {
  getTeachingPlanPath,
  usesRoutineTimetable,
  ncPathHeading,
} from '../lib/teachingPlanRouting';
import { isRoutineTimetable } from '../lib/routineSlotFiller';

function currentTermId() {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  if (currentMonth >= 8 && currentMonth <= 11) return `autumn-${currentYear}`;
  if (currentMonth >= 0 && currentMonth <= 3) return `spring-${currentYear + 1}`;
  return `summer-${currentYear + 1}`;
}

const TeachingPlanPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const termFromQuery = searchParams.get('term');
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savedPlan, setSavedPlan] = useState(null);
  const [savedTimetable, setSavedTimetable] = useState(null);
  const [currentTermIdState, setCurrentTermIdState] = useState(null);
  const [savingTimetable, setSavingTimetable] = useState(false);
  const [activeTab, setActiveTab] = useState('activities');

  const planFromState = location.state?.plan;
  const studentNameFromState = location.state?.studentName;
  const termIdFromState = location.state?.termId;

  const ncStatus = student?.questionnaireAnswers?.ncStatus ?? savedPlan?.ncStatus;
  const planPath = useMemo(() => {
    if (savedPlan?.routingPath) {
      return savedPlan.routingPath === 'below_nc_routine' ? 'below_nc_routine' : 'nc_interventions';
    }
    return getTeachingPlanPath(ncStatus);
  }, [savedPlan?.routingPath, ncStatus]);

  const showRoutineTimetable = planPath === 'below_nc_routine';

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const fetchStudentAndPlan = async () => {
      try {
        setLoading(true);
        const studentSnap = await getDoc(doc(db, 'students', id));
        if (!studentSnap.exists()) {
          navigate('/student-organization');
          return;
        }
        if (cancelled) return;
        const studentData = { id: studentSnap.id, ...studentSnap.data() };
        setStudent(studentData);

        const termIdForTimetable = termIdFromState || termFromQuery || currentTermId();

        if (planFromState) {
          setSavedPlan(planFromState);
          setCurrentTermIdState(termIdFromState || termIdForTimetable);
        } else {
          setCurrentTermIdState(termIdForTimetable);

          try {
            const planSnap = await getDoc(doc(db, 'teachingPlans', id, 'terms', termIdForTimetable));
            if (!cancelled && planSnap.exists()) {
              setSavedPlan({ id: planSnap.id, ...planSnap.data() });
            }
          } catch (planError) {
            console.warn('Could not load saved teaching plan for term', termIdForTimetable, planError?.message);
          }
        }

        if (usesRoutineTimetable(studentData?.questionnaireAnswers?.ncStatus)) {
          try {
            const ttSnap = await getDoc(doc(db, 'students', id, 'timetables', termIdForTimetable));
            if (!cancelled && ttSnap.exists()) {
              const data = ttSnap.data();
              if (isRoutineTimetable(data)) {
                setSavedTimetable(data);
              } else if (data.timetable) {
                setSavedTimetable(null);
              }
            }
          } catch (ttErr) {
            console.warn('Could not load routine timetable', ttErr?.message);
          }
        }
      } catch (error) {
        console.error('Error fetching student:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchStudentAndPlan();
    return () => {
      cancelled = true;
    };
  }, [id, navigate, planFromState, termIdFromState, termFromQuery]);

  const handleTimetableSave = async (timetable) => {
    if (!id || !currentTermIdState) return;

    try {
      setSavingTimetable(true);
      await setDoc(
        doc(db, 'students', id, 'timetables', currentTermIdState),
        {
          ...timetable,
          updatedAt: new Date(),
          studentId: id,
          termId: currentTermIdState,
        },
        { merge: true }
      );
      setSavedTimetable(timetable);
    } catch (error) {
      console.error('Error saving timetable:', error);
    } finally {
      setSavingTimetable(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto" />
            <p className="mt-4 text-gray-600">Loading teaching plan...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!student) {
    return (
      <AppLayout>
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-gray-900">Student not found</h1>
        </div>
      </AppLayout>
    );
  }

  const studentName = studentNameFromState || `${student.firstName} ${student.lastName}`.trim();
  const termLabel = currentTermIdState?.replace('-', ' ').replace(/\b\w/g, (l) => l.toUpperCase());

  if (savedPlan) {
    const structuredActivities = (savedPlan.activities || []).filter(
      (a) => a.activityType === 'structured' || !a.activityType
    );

    return (
      <AppLayout>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="mb-6">
            <p className="text-sm text-gray-500 mb-1">
              <Link to={`/student/${id}?tab=teaching-plan`} className="text-indigo-600 hover:underline">
                Back to ISP — Teaching Plan
              </Link>
            </p>
            <h1 className="text-3xl font-bold mb-2">Teaching Plan for {studentName}</h1>
            <p className="text-gray-600">
              Term: {termLabel}.{' '}
              {showRoutineTimetable
                ? 'Matched activities and a suggested differentiated daily routine from the classroom questionnaire.'
                : 'Matched activities and strategies from the classroom questionnaire.'}
            </p>
            <p className="mt-2 text-sm text-gray-500">
              <Link to={`/students/${id}/one-page-profile`} className="text-indigo-600 hover:underline">
                One-Page Profile
              </Link>
            </p>
          </div>

          {showRoutineTimetable ? (
            <div className="border-b border-gray-200 mb-6">
              <nav className="-mb-px flex space-x-8">
                <button
                  type="button"
                  onClick={() => setActiveTab('activities')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'activities'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Activity Tiles ({savedPlan?.activities?.length || 0})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('timetable')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'timetable'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Suggested daily routine
                </button>
              </nav>
            </div>
          ) : null}

          {activeTab === 'activities' && (
            <div>
              {!showRoutineTimetable && (
                <h2 className="text-lg font-semibold text-gray-800 mb-4">{ncPathHeading(studentName)}</h2>
              )}
              <TeachingPlanTiles
                activities={savedPlan.activities}
                studentName={studentName}
                hideTitle
              />
            </div>
          )}

          {showRoutineTimetable && activeTab === 'timetable' && (
            <div>
              <p className="text-gray-700 mb-4">
                This is a <strong>suggested differentiated daily routine</strong> for {studentName}. Anchor
                blocks repeat every weekday; only the variation slots change across the week. Edit and save
                to match your classroom.
              </p>
              {structuredActivities.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <p className="text-gray-500">No structured activities available for the routine.</p>
                  </CardContent>
                </Card>
              ) : (
                <RoutineTimetableView
                  activities={structuredActivities}
                  student={student}
                  termId={currentTermIdState}
                  savedTimetable={savedTimetable}
                  templateId={savedPlan.templateId}
                  onSave={handleTimetableSave}
                  saving={savingTimetable}
                />
              )}
            </div>
          )}

          <div className="mt-8 flex justify-between">
            <Link
              to={`/student/${id}?tab=teaching-plan`}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Back to ISP
            </Link>
            <button
              type="button"
              onClick={() => {
                setSavedPlan(null);
                navigate(`/students/${id}/teaching-plan-questionnaire`);
              }}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Regenerate Plan
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-100 flex items-center justify-center">
            <Icons.BookOpen className="w-8 h-8 text-gray-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">No Teaching Plan Yet</h1>
          <p className="text-gray-600 mb-4 max-w-lg mx-auto">
            Complete the classroom questionnaire to generate a term teaching plan with matched activities
            {usesRoutineTimetable(student?.questionnaireAnswers?.ncStatus)
              ? ' and a suggested differentiated daily routine'
              : ' and strategies to fit into the class timetable'}{' '}
            for {studentName}.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            <Link to={`/students/${id}/one-page-profile`} className="text-indigo-600 hover:underline">
              One-Page Profile
            </Link>
          </p>
          <button
            type="button"
            onClick={() => navigate(`/students/${id}/teaching-plan-questionnaire`)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Start Questionnaire
          </button>
        </div>
      </div>
    </AppLayout>
  );
};

export default TeachingPlanPage;
